import React, { useState } from 'react';
import { useTranslation } from '../../../../hooks';
import styles from './FAQ.module.css';

/**
 * FAQ Component (Landing Feature)
 * 
 * Frequently Asked Questions section component featuring:
 * - Expandable/collapsible question items
 * - Smooth animations and transitions
 * - RTL language support
 * - Keyboard navigation support
 * - CSS Modules for scoped styling
 * - Accessibility features
 * 
 * @component
 * @example
 * return (
 *   <FAQ />
 * )
 */
const FAQ = () => {
  const { t, direction, isArabic } = useTranslation();
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const toggleItem = (index) => {
    setOpenItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleItem(index);
    }
  };

  return (
    <section 
      className={styles.faqSection} 
      style={{ direction }}
      aria-labelledby="faq-title"
    >
      <div className={styles.container}>
        <h2 id="faq-title" className={styles.title}>
          {t.faq.title}
        </h2>

        <p className={styles.subtitle}>
          {t.faq.subtitle}
        </p>

        <div className={styles.faqContainer} role="region" aria-labelledby="faq-title">
          {t.faq.questions.map((item, index) => (
            <div key={index} className={`${styles.faqItem} ${openItems.has(index) ? styles.open : ''}`}>
                <button
                  className={styles.faqQuestion}
                  onClick={() => toggleItem(index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  aria-expanded={openItems.has(index)}
                  aria-controls={`faq-answer-${index}`}
                  id={`faq-question-${index}`}
                  style={{ 
                    direction,
                    textAlign: isArabic ? 'right' : 'left'
                  }}
                >
                  <span 
                    className={`${styles.faqQuestionText} ${isArabic ? styles.rtl : ''}`}
                    style={{ 
                      direction,
                      textAlign: isArabic ? 'right' : 'left'
                    }}
                  >
                    {item.question}
                  </span>
                  <div 
                    className={`${styles.faqIcon} ${openItems.has(index) ? styles.rotated : ''} ${isArabic ? styles.rtl : ''}`}
                    style={{
                      marginLeft: isArabic ? '0' : '16px',
                      marginRight: isArabic ? '16px' : '0'
                    }}
                  >
                    <svg 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      aria-hidden="true"
                    >
                      <path 
                        d="M6 9l6 6 6-6" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>
                
                <div 
                  id={`faq-answer-${index}`}
                  className={`${styles.faqAnswer} ${openItems.has(index) ? styles.open : ''}`}
                  role="region"
                  aria-labelledby={`faq-question-${index}`}
                  style={{
                    direction
                  }}
                >
                  <div 
                    className={`${styles.faqAnswerContent} ${isArabic ? styles.rtl : ''}`}
                    style={{ 
                      direction,
                      textAlign: isArabic ? 'right' : 'left'
                    }}
                  >
                    {item.answer}
                  </div>
                </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;